import {
  Alert,
  Box,
  Button,
  Card,
  IconButton,
  InputAdornment,
  Skeleton,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useEffect, useMemo, useState } from "react";
import { useCurrentProfile } from "../../../../../ContextWrapper";
import { get } from "../../../../../api/api";
import { resetPassword } from "../../../../../api/authentication";

const buildPasswordRequirements = (value) => {
  const str = String(value || "");
  return {
    hasMinLength: str.length >= 8,
    hasUpper: /[A-Z]/.test(str),
    hasLower: /[a-z]/.test(str),
    hasNumber: /\d/.test(str),
    hasSymbol: /[^a-zA-Z0-9]/.test(str),
  };
};

const buildHelperText = ({ currentPassword, newPassword, confirmPassword, submitError }) => {
  if (submitError) return submitError;
  if (!currentPassword && (newPassword || confirmPassword)) return "Enter your current password.";
  if (!newPassword && confirmPassword) return "Enter a new password.";
  if (currentPassword && newPassword && currentPassword === newPassword)
    return "New password cannot be the same as your current password.";
  if (newPassword && confirmPassword && newPassword !== confirmPassword)
    return "New passwords do not match.";
  return "";
};

const PasswordField = ({
  label,
  placeholder,
  value,
  onChange,
  show,
  onToggleShow,
  autoComplete,
  inputSx,
  showFieldError,
  helperText,
}) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
      <Typography
        sx={{
          fontFamily: "Rethink Sans",
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "143%",
          letterSpacing: "0.17px",
          color: "rgba(0,0,0,0.6)",
        }}
      >
        {label}
      </Typography>
      <TextField
        fullWidth
        type={show ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        error={showFieldError}
        helperText={helperText}
        FormHelperTextProps={{
          sx: {
            mt: "6px",
            fontFamily: "Rethink Sans",
            lineHeight: 1.2,
            minHeight: "16px",
          },
        }}
        sx={inputSx}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => onToggleShow((v) => !v)}
                edge="end"
                size="small"
                sx={{ mr: "-4px" }}
              >
                {show ? (
                  <VisibilityOutlinedIcon sx={{ width: 16, height: 16 }} />
                ) : (
                  <VisibilityOffOutlinedIcon sx={{ width: 16, height: 16 }} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

export default function PasswordSettings({ onLoadingChange } = {}) {
  const { profile } = useCurrentProfile();
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  useEffect(() => {
    if (!profile?.id) return;
    let active = true;
    setLoading(true);

    void (async () => {
      try {
        const fullProfile = await get(`/api/v2/profiles/${profile.id}/`);
        if (!active) return;
        setEmail((fullProfile?.user?.email || "").trim());
      } catch (err) {
        console.error("Failed loading profile for password settings:", err);
      } finally {
        if (active) {
          setLoading(false);
          onLoadingChange?.(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [profile?.id, onLoadingChange]);

  const reqs = useMemo(() => buildPasswordRequirements(newPassword), [newPassword]);
  const isValidNewPassword =
    reqs.hasMinLength &&
    reqs.hasUpper &&
    reqs.hasLower &&
    reqs.hasNumber &&
    reqs.hasSymbol;

  const canSave =
    Boolean(email) &&
    Boolean(currentPassword) &&
    Boolean(newPassword) &&
    Boolean(confirmPassword) &&
    isValidNewPassword &&
    newPassword !== currentPassword &&
    newPassword === confirmPassword &&
    !saving;

  const helperText = useMemo(
    () => buildHelperText({ currentPassword, newPassword, confirmPassword, submitError }),
    [currentPassword, newPassword, confirmPassword, submitError]
  );
  const showFieldError = Boolean(helperText);

  const requirementRows = useMemo(
    () => [
      {
        key: "num-symbol",
        met: reqs.hasNumber && reqs.hasSymbol,
        label: "At least one number and symbol (e.g. #$%@*)",
      },
      {
        key: "min-length",
        met: reqs.hasMinLength,
        label: "Minimum 8 characters long - the more, the better",
      },
      {
        key: "upper-lower",
        met: reqs.hasUpper && reqs.hasLower,
        label: "A mix of upper & lowercase letters",
      },
    ],
    [reqs.hasLower, reqs.hasMinLength, reqs.hasNumber, reqs.hasSymbol, reqs.hasUpper]
  );

  const inputSx = useMemo(
    () => ({
      fontFamily: "Rethink Sans",
      "& .MuiOutlinedInput-root": {
        height: "40px",
        borderRadius: "8px",
        alignItems: "center",
        "& fieldset": { borderColor: "#E0E0E0" },
        "&.Mui-focused fieldset": { borderColor: "primary.main" },
      },
      "& .MuiOutlinedInput-input": {
        padding: "0 12px",
        height: "40px",
        boxSizing: "border-box",
        fontSize: "14px",
        lineHeight: "40px",
      },
    }),
    []
  );

  const resetForm = () => {
    setSubmitError("");
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
  };

  const onSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSubmitError("");

    try {
      const res = await resetPassword({
        email,
        oldPassword: currentPassword,
        password: newPassword,
      });

      if (res?.data?.reset) {
        resetForm();
        setSnackbarMsg("Password updated successfully.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        setSnackbarMsg(
          "Failed to update password. Please ensure your old password is correct."
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error("Failed updating password:", err);
      setSnackbarMsg("Failed to update password. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card
        sx={{
          borderRadius: "12px",
          border: "1px solid #E0E0E0",
          boxShadow: "0px 1px 4px 0px rgba(0,0,0,0.04)",
          overflow: "hidden",
          width: "100%",
        }}
      >
        {/* Header skeleton */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            px: "24px",
            py: "14px",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            backgroundColor: "#fff",
          }}
        >
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={160} height={28} sx={{ flex: 1 }} />
        </Box>

        {/* Form fields skeleton */}
        <Box
          sx={{
            px: "24px",
            pt: "20px",
            pb: "24px",
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "24px", maxWidth: "470px", width: "100%" }}>
            {["Current Password", "New Password", "Confirm Password"].map((label) => (
              <Box key={label} sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <Skeleton variant="text" width={120} height={18} />
                <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: "8px" }} />
              </Box>
            ))}
          </Box>

          {/* Requirements skeleton */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", maxWidth: "470px", width: "100%" }}>
            <Skeleton variant="text" width={160} height={18} />
            {[140, 220, 180].map((w, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Skeleton variant="circular" width={18} height={18} />
                <Skeleton variant="text" width={w} height={18} />
              </Box>
            ))}
          </Box>

          {/* Button skeletons */}
          <Box sx={{ display: "flex", gap: "16px", justifyContent: "flex-end" }}>
            <Skeleton variant="rounded" width={90} height={38} sx={{ borderRadius: "8px" }} />
            <Skeleton variant="rounded" width={90} height={38} sx={{ borderRadius: "8px" }} />
          </Box>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: "12px",
        border: "1px solid #E0E0E0",
        boxShadow: "0px 1px 4px 0px rgba(0,0,0,0.04)",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: "24px",
          py: "14px",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          backgroundColor: "#fff",
        }}
      >
        <LockOutlinedIcon sx={{ color: "#536DEC", fontSize: 20 }} />
        <Typography
          sx={{
            fontFamily: "Rethink Sans",
            fontWeight: 600,
            fontSize: "18px",
            lineHeight: "175%",
            letterSpacing: "0.15px",
            color: "rgba(0,0,0,0.87)",
            flex: 1,
          }}
        >
          Password Settings
        </Typography>
      </Box>

      <Box
        sx={{
          px: "24px",
          pt: "20px",
          pb: "24px",
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          alignItems: "flex-end",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "36px",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "470px",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
            }}
          >
            <PasswordField
              label="Current Password"
              placeholder="Enter Current Password"
              value={currentPassword}
              onChange={(v) => {
                setSubmitError("");
                setCurrentPassword(v);
              }}
              show={showCurrent}
              onToggleShow={setShowCurrent}
              autoComplete="current-password"
              inputSx={inputSx}
              showFieldError={showFieldError}
              helperText={" "}
            />
            <PasswordField
              label="New Password"
              placeholder="Enter New Password"
              value={newPassword}
              onChange={(v) => {
                setSubmitError("");
                setNewPassword(v);
              }}
              show={showNew}
              onToggleShow={setShowNew}
              autoComplete="new-password"
              inputSx={inputSx}
              showFieldError={showFieldError}
              helperText={" "}
            />
            <PasswordField
              label="Confirm Password"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(v) => {
                setSubmitError("");
                setConfirmPassword(v);
              }}
              show={showConfirm}
              onToggleShow={setShowConfirm}
              autoComplete="new-password"
              inputSx={inputSx}
              showFieldError={showFieldError}
              helperText={helperText}
            />
          </Box>

          <Box
            sx={{
              width: "100%",
              maxWidth: "470px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Rethink Sans",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "143%",
                letterSpacing: "0.17px",
                color: "rgba(0,0,0,0.6)",
              }}
            >
              Password Requirements:
            </Typography>
            <Box
              component="ul"
              sx={{
                listStyle: "none",
                p: 0,
                m: 0,
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              {requirementRows.map((r) => {
                const color = r.met ? "rgba(0,0,0,0.87)" : "rgba(0,0,0,0.38)";
                const iconColor = r.met ? "#536DEC" : "rgba(0,0,0,0.24)";

                return (
                  <Box
                    key={r.key}
                    component="li"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      color,
                    }}
                  >
                    {r.met ? (
                      <CheckCircleOutlineIcon sx={{ fontSize: 18, color: iconColor }} />
                    ) : (
                      <RadioButtonUncheckedOutlinedIcon
                        sx={{ fontSize: 18, color: iconColor }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontFamily: "Rethink Sans",
                        fontWeight: 500,
                        fontSize: "15px",
                        lineHeight: "143%",
                        letterSpacing: "0.17px",
                        color: "inherit",
                      }}
                    >
                      {r.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "16px" }}>
          <Button
            variant="outlined"
            onClick={resetForm}
            disabled={saving}
            sx={{
              borderRadius: "8px",
              px: "22px",
              py: "8px",
              textTransform: "capitalize",
              borderColor: "rgba(83,109,236,0.5)",
              color: "#536DEC",
              fontFamily: "Rethink Sans",
              fontWeight: 500,
              fontSize: "15px",
              lineHeight: "26px",
              letterSpacing: "0.46px",
              "&:hover": {
                borderColor: "rgba(83,109,236,0.5)",
                backgroundColor: "rgba(83,109,236,0.04)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!canSave}
            sx={{
              borderRadius: "8px",
              px: "22px",
              py: "8px",
              textTransform: "capitalize",
              backgroundColor: canSave ? "#536DEC" : "rgba(0,0,0,0.12)",
              color: canSave ? "#fff" : "rgba(0,0,0,0.38)",
              boxShadow: canSave
                ? "0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.2)"
                : "none",
              fontFamily: "Rethink Sans",
              fontWeight: 500,
              fontSize: "15px",
              lineHeight: "26px",
              letterSpacing: "0.46px",
              "&:hover": {
                backgroundColor: canSave ? "#4C63D7" : "rgba(0,0,0,0.12)",
              },
            }}
          >
            Save
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setSnackbarOpen(false);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%", fontFamily: "Rethink Sans" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Card>
  );
}

